import React, { useState } from 'react';
import AppPagePanel from 'components/ui/AppPagePanel';
import AddToTreeModal, { TreeOption } from 'components/modals/AddToTreeModal';
import SuggestedOpportunityTile from './SuggestedOpportunityTile';
import { SuggestedOpportunity } from 'types';
import { addSuggestedOpportunities } from 'api/projects';
import { isErrorResponse } from 'utils/apiUtils';

interface Props {
    projectId: string;
    opportunities: SuggestedOpportunity[];
    trees: TreeOption[];
    isGenerating: boolean;
    onOpportunitiesAdded: () => void;
    onError: (msg: string) => void;
}

export default function OpportunitySuggestionsPanel({
    projectId,
    opportunities,
    trees,
    isGenerating,
    onOpportunitiesAdded,
    onError,
}: Props) {
    const [showModal, setShowModal] = useState(false);
    const [isAdding, setIsAdding] = useState(false);

    const canAdd = opportunities.length > 0 && !isGenerating && trees.length > 0;

    const handleAddToTree = async (selectedTreeId: string | null) => {
        setIsAdding(true);
        try {
            const result = await addSuggestedOpportunities(projectId, selectedTreeId, opportunities);
            // Close the modal either way: on success we're done; on error (e.g. the tree
            // has no outcome yet) the toast must be visible and the suggestions stay put.
            setShowModal(false);
            if (isErrorResponse(result)) {
                onError((result as any).error || 'Failed to add opportunities.');
            } else {
                onOpportunitiesAdded();
            }
        } finally {
            setIsAdding(false);
        }
    };

    // Render tiles with parents first, then each parent's children indented below it.
    const parents = opportunities.filter((o) => !o.parentTempId);
    const childrenByParent: Record<string, SuggestedOpportunity[]> = {};
    for (const opp of opportunities) {
        if (opp.parentTempId) {
            (childrenByParent[opp.parentTempId] = childrenByParent[opp.parentTempId] || []).push(opp);
        }
    }

    const addButton = (
        <button
            className={`opp-suggestions-panel__add-btn${canAdd ? '' : ' opp-suggestions-panel__add-btn--disabled'}`}
            onClick={() => canAdd && setShowModal(true)}
            disabled={!canAdd}
            title={trees.length === 0 ? 'No trees available in this project' : 'Add all to a tree'}
            aria-label='Add opportunities to a tree'
        >
            Commit Suggestions to Tree
        </button>
    );

    return (
        <AppPagePanel
            title='Opportunity Suggestions'
            style={{ minWidth: 0, flex: 1, minHeight: 0 }}
            className='opp-suggestions-panel'
        >
            <div className='opp-suggestions-panel__commit-corner'>
                {addButton}
            </div>
            {isGenerating && (
                <p className='opp-suggestions-panel__status'>Identifying opportunities…</p>
            )}

            {!isGenerating && opportunities.length === 0 && (
                <div className='opp-suggestions-panel__empty-box'>
                    <p className='opp-suggestions-panel__empty'>
                        Generate an insights summary to<br />identify opportunity suggestions.
                    </p>
                </div>
            )}

            {!isGenerating && opportunities.length > 0 && (
                <div className='opp-suggestions-panel__list'>
                    {parents.map((parent) => (
                        <React.Fragment key={parent.tempId}>
                            <SuggestedOpportunityTile opportunity={parent} isChild={false} />
                            {(childrenByParent[parent.tempId] || []).map((child) => (
                                <SuggestedOpportunityTile key={child.tempId} opportunity={child} isChild={true} />
                            ))}
                        </React.Fragment>
                    ))}
                </div>
            )}

            {showModal && (
                <AddToTreeModal
                    trees={trees}
                    onConfirm={handleAddToTree}
                    onClose={() => !isAdding && setShowModal(false)}
                    isAdding={isAdding}
                />
            )}
        </AppPagePanel>
    );
}
